import { useState, useEffect } from 'react';
import { styles } from './styles';
import { api } from './api';
import LoginScreen from './screens/LoginScreen';
import UploadScreen from './screens/UploadScreen';
import ManualEntryScreen from './screens/ManualEntryScreen';
import ReviewScreen from './screens/ReviewScreen';
import DashboardScreen from './screens/DashboardScreen';
import BudgetSettingsScreen from './screens/BudgetSettingsScreen';
import CategoryDetailScreen from './screens/CategoryDetailScreen';
import CategoryManageScreen from './screens/CategoryManageScreen';
import CategoryEditScreen from './screens/CategoryEditScreen';
import ReceiptEditScreen from './screens/ReceiptEditScreen';
import UserPicker from './components/UserPicker';
import HistoryScreen from './screens/HistoryScreen';
import ReceiptsScreen from './screens/ReceiptsScreen';
import NotificationsScreen from './screens/NotificationsScreen';
import AlertSettingsScreen from './screens/AlertSettingsScreen';
import StatementUploadScreen from './screens/StatementUploadScreen';
import StatementReviewScreen from './screens/StatementReviewScreen';
import MortgageScreen from './screens/MortgageScreen';
import MortgageSetupScreen from './screens/MortgageSetupScreen';
import MortgagePaymentScreen from './screens/MortgagePaymentScreen';

function App() {
  const [authenticated, setAuthenticated] = useState(null);
  const [currentUser, setCurrentUser] = useState(
    localStorage.getItem('currentUserId') || '00000000-0000-0000-0000-000000000001'
  );
  const [screen, setScreen] = useState('dashboard');
  const [lastReceipt, setLastReceipt] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBudgetInfo, setSelectedBudgetInfo] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [dashboardMonth, setDashboardMonth] = useState({ 
    year: new Date().getFullYear(), 
    month: new Date().getMonth() + 1 
  });
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingParentCategory, setEditingParentCategory] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [receiptEditReturnScreen, setReceiptEditReturnScreen] = useState('categoryDetail');
  const [statementTransactions, setStatementTransactions] = useState(null);

  useEffect(() => {
    api.get('/auth/check')
      .then(() => setAuthenticated(true))
      .catch(() => setAuthenticated(false));
  }, []);

  const handleUserChange = (userId) => {
    setCurrentUser(userId);
    localStorage.setItem('currentUserId', userId);
    setRefreshKey(k => k + 1);
  };

  const navigate = (newScreen, data) => {
    if (data) setLastReceipt(data);
    if (newScreen === 'dashboard') setRefreshKey(k => k + 1);
    setScreen(newScreen);
  };

  const handleCategorySelect = (category, budgetInfo, month) => {
    setSelectedCategory(category);
    setSelectedBudgetInfo(budgetInfo);
    setSelectedMonth(month);
    setScreen('categoryDetail');
  };

  const handleReceiptSelect = (receipt, returnTo = 'categoryDetail') => {
    setSelectedReceipt(receipt);
    setReceiptEditReturnScreen(returnTo);
    setScreen('receiptEdit');
  };

  const handleCategoryEdit = (category, parentCategory = null) => {
    setEditingCategory(category);
    setEditingParentCategory(parentCategory);
    setScreen('categoryEdit');
  };

  const handleAddSubCategory = (parentCategory) => {
    setEditingCategory(null);
    setEditingParentCategory(parentCategory);
    setScreen('categoryEdit');
  };

  if (authenticated === null) {
    return <div style={styles.app}>Loading...</div>;
  }

  if (!authenticated) {
    return <LoginScreen onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <div style={styles.app}>
      {/* User picker - shown on dashboard */}
      {screen === 'dashboard' && (
        <div style={{ padding: '10px 15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
          <UserPicker value={currentUser} onChange={handleUserChange} />
        </div>
      )}

      {screen === 'upload' && (
        <UploadScreen
          currentUser={currentUser}
          onSuccess={(r) => navigate('review', r)}
          onManualEntry={() => navigate('manual')}
          onImportStatement={() => navigate('statementUpload')}
          onBack={() => navigate('dashboard')}
        />
      )}
      {screen === 'manual' && (
        <ManualEntryScreen 
          currentUser={currentUser}
          onSuccess={() => navigate('dashboard')} 
          onCancel={() => navigate('upload')}
        />
      )}
      {screen === 'review' && (
        <ReviewScreen 
          receipt={lastReceipt} 
          onDone={() => navigate('dashboard')} 
        />
      )}
      {screen === 'dashboard' && (
        <DashboardScreen
          key={refreshKey}
          currentUser={currentUser}
          onAdd={() => navigate('upload')}
          onSettings={() => navigate('settings')}
          onHistory={() => navigate('history')}
          onNotifications={() => navigate('notifications')}
          onReceipts={() => navigate('receipts')}
          onCategorySelect={handleCategorySelect}
          onMonthChange={setDashboardMonth}
          onMortgage={() => navigate('mortgage')}
        />
      )}
      {screen === 'settings' && (
        <BudgetSettingsScreen 
          month={dashboardMonth}
          onBack={() => navigate('dashboard')}
          onManageCategories={() => navigate('categoryManage')}
          onAlertSettings={() => navigate('alertSettings')}
        />
      )}
      {screen === 'categoryDetail' && (
        <CategoryDetailScreen
          category={selectedCategory}
          budgetInfo={selectedBudgetInfo}
          month={selectedMonth}
          onBack={() => navigate('dashboard')}
          onReceiptSelect={handleReceiptSelect}
        />
      )}
      {screen === 'categoryManage' && (
        <CategoryManageScreen
          onBack={() => navigate('settings')}
          onEdit={handleCategoryEdit}
          onAddSubCategory={handleAddSubCategory}
        />
      )}
      {screen === 'categoryEdit' && (
        <CategoryEditScreen
          category={editingCategory}
          parentCategory={editingParentCategory}
          onBack={() => { setEditingParentCategory(null); navigate('categoryManage'); }}
          onSaved={() => { setEditingParentCategory(null); navigate('categoryManage'); }}
          onAddSubCategory={handleAddSubCategory}
        />
      )}
      {screen === 'receiptEdit' && (
        <ReceiptEditScreen
          receipt={selectedReceipt}
          onBack={() => navigate(receiptEditReturnScreen)}
          onSaved={() => {
            setSelectedReceipt(null);
            navigate(receiptEditReturnScreen);
          }}
        />
      )}

      {screen === 'history' && (
        <HistoryScreen onBack={() => navigate('dashboard')} />
      )}

      {screen === 'receipts' && (
        <ReceiptsScreen
          onBack={() => navigate('dashboard')}
          onReceiptSelect={(r) => handleReceiptSelect(r, 'receipts')}
        />
      )}

      {screen === 'notifications' && (
        <NotificationsScreen onBack={() => navigate('dashboard')} />
      )}
      
      {screen === 'alertSettings' && (
        <AlertSettingsScreen onBack={() => navigate('settings')} />
      )}

      {screen === 'statementUpload' && (
        <StatementUploadScreen
          currentUser={currentUser}
          onScanned={(transactions) => {
            setStatementTransactions(transactions);
            navigate('statementReview');
          }}
          onBack={() => navigate('upload')}
        />
      )}

      {screen === 'statementReview' && (
        <StatementReviewScreen
          transactions={statementTransactions}
          currentUser={currentUser}
          onDone={() => navigate('dashboard')}
          onBack={() => navigate('statementUpload')}
        />
      )}

      {screen === 'mortgage' && (
        <MortgageScreen
          onBack={() => navigate('dashboard')}
          onLogPayment={() => navigate('mortgagePayment')}
          onSetup={() => navigate('mortgageSetup')}
        />
      )}
      {screen === 'mortgageSetup' && (
        <MortgageSetupScreen
          onBack={() => navigate('mortgage')}
          onSave={() => navigate('mortgage')}
        />
      )}
      {screen === 'mortgagePayment' && (
        <MortgagePaymentScreen
          currentUser={currentUser}
          onBack={() => navigate('mortgage')}
          onSave={() => navigate('mortgage')}
        />
      )}
    </div>
  );
}

export default App;